import {
  Building2,
  FileText,
  Flag,
  Globe,
  Hash,
  Mail,
  MapPin,
  Phone,
  ScrollText,
  User,
} from 'lucide-react';

import type { MatchProvidedDetails, MatchProvidedPrincipal } from '../types';
import { ReportCard } from '../../composites';
import { DataRow } from '../../primitives';

const PrincipalBlock = ({
  principal,
  index,
}: {
  principal: MatchProvidedPrincipal;
  index: number;
}) => (
  <div className="rounded-lg border border-slate-200 p-4">
    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
      <User className="h-4 w-4" />
      {principal.name || `Principal ${index + 1}`}
    </div>
    <div className="grid grid-cols-1 gap-x-12 md:grid-cols-2">
      <DataRow
        label="Email"
        Icon={Mail}
        value={principal.email}
        copyable
        copyValue={principal.email ?? ''}
        emptyLabel="Not Provided"
        disableTranslation
      />
      <DataRow
        label="Phone Number"
        Icon={Phone}
        value={principal.phoneNumber}
        copyable
        copyValue={principal.phoneNumber ?? ''}
        emptyLabel="Not Provided"
      />
      <DataRow
        label="Address"
        Icon={MapPin}
        value={principal.address}
        copyable
        copyValue={principal.address ?? ''}
        emptyLabel="Not Provided"
      />
    </div>
  </div>
);

/** The "Provided Details" section: the merchant + principal data submitted with the inquiry. */
export const MatchProvidedDetailsSection = ({
  providedDetails,
}: {
  providedDetails?: MatchProvidedDetails | null;
}) => {
  const details = providedDetails ?? {};
  const principals = details.principals ?? [];
  const websites = details.websites?.length ? details.websites.join(', ') : null;

  return (
    <ReportCard title="Provided Details" Icon={FileText} showTooltip={false}>
      <div className="grid grid-cols-1 gap-x-12 md:grid-cols-2">
        <DataRow
          label="Merchant Name"
          Icon={Building2}
          value={details.merchantName}
          copyable
          copyValue={details.merchantName ?? ''}
          emptyLabel="Not Provided"
          disableTranslation
        />
        <DataRow label="MCC" Icon={ScrollText} value={details.mcc} emptyLabel="Not Provided" />
        <DataRow label="DBA" Icon={Building2} value={details.dba} emptyLabel="Not Provided" />
        <DataRow
          label="National Tax ID"
          Icon={Hash}
          value={details.nationalTaxId}
          emptyLabel="Not Provided"
        />
        <DataRow
          label="Phone Number"
          Icon={Phone}
          value={details.phoneNumber}
          copyable
          copyValue={details.phoneNumber ?? ''}
          emptyLabel="Not Provided"
        />
        <DataRow
          label="Alt Phone Number"
          Icon={Phone}
          value={details.altPhoneNumber}
          copyable
          copyValue={details.altPhoneNumber ?? ''}
          emptyLabel="Not Provided"
        />
        <DataRow
          label="Address"
          Icon={MapPin}
          value={details.address}
          copyable
          copyValue={details.address ?? ''}
          emptyLabel="Not Provided"
        />
        <DataRow
          label="State / Region"
          Icon={MapPin}
          value={details.state}
          emptyLabel="Not Provided"
        />
        <DataRow label="Country" Icon={Flag} value={details.country} emptyLabel="Not Provided" />
        <DataRow
          label="Postal Code"
          Icon={Hash}
          value={details.postalCode}
          emptyLabel="Not Provided"
        />
        <DataRow
          label="Website(s)"
          Icon={Globe}
          value={websites}
          copyable
          copyValue={websites ?? ''}
          emptyLabel="Not Provided"
          disableTranslation
        />
      </div>

      {principals.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-bold text-slate-900">Principals</h4>
          {principals.map((principal, index) => (
            <PrincipalBlock
              key={`${principal.name ?? 'principal'}-${index}`}
              principal={principal}
              index={index}
            />
          ))}
        </div>
      )}
    </ReportCard>
  );
};
